import React, { useEffect, useState } from 'react';

// PECR / UK GDPR: non-essential cookies and analytics can only fire after
// the user has given explicit consent. FamHearth currently only uses strictly
// necessary storage on the marketing site (session, preference), so the banner
// mostly records the user's choice for future feature additions.

const STORAGE_KEY = 'fh_cookie_consent_v1';

type Consent = 'accepted' | 'rejected' | null;

const readConsent = (): Consent => {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'accepted' || v === 'rejected' ? v : null;
  } catch {
    return null;
  }
};

const writeConsent = (v: Consent) => {
  try {
    if (v) window.localStorage.setItem(STORAGE_KEY, v);
  } catch { /* storage may be disabled */ }
};

const isAppRoute = () => {
  const p = window.location.pathname;
  return p.startsWith('/app') || p.startsWith('/family-setup') || p === '/login' || p === '/register';
};

const CookieBanner: React.FC = () => {
  const [consent, setConsent] = useState<Consent>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const current = readConsent();
    setConsent(current);
    // Only show on public marketing pages; logged-in app surfaces already
    // agreed to terms and privacy at signup.
    setVisible(current === null && !isAppRoute());
  }, []);

  const accept = () => { writeConsent('accepted'); setConsent('accepted'); setVisible(false); };
  const reject = () => { writeConsent('rejected'); setConsent('rejected'); setVisible(false); };

  if (!visible || consent) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
      style={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: 9999,
        maxWidth: 640,
        margin: '0 auto',
        background: '#1c1f24',
        color: '#f5f3ee',
        borderRadius: 14,
        border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 20px 48px rgba(0,0,0,0.4)',
        padding: '18px 18px 16px',
        fontFamily: 'Manrope, system-ui, sans-serif',
        fontSize: 14,
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 15 }}>Cookies &amp; privacy</div>
      <p style={{ margin: '0 0 12px', color: 'rgba(245,243,238,0.75)' }}>
        We use strictly-necessary cookies to keep FamHearth working. We don't use tracking cookies or ad networks. You can read our{' '}
        <a href="/privacy" style={{ color: '#e9a179', textDecoration: 'underline' }}>privacy policy</a> or{' '}
        <a href="/terms" style={{ color: '#e9a179', textDecoration: 'underline' }}>terms</a>.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          onClick={reject}
          style={{
            flex: '1 1 120px', padding: '10px 14px', borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.2)', background: 'transparent',
            color: '#f5f3ee', fontWeight: 600, cursor: 'pointer',
          }}
        >
          Reject non-essential
        </button>
        <button
          onClick={accept}
          style={{
            flex: '1 1 120px', padding: '10px 14px', borderRadius: 10,
            border: 'none', background: '#c5512d', color: '#fff',
            fontWeight: 700, cursor: 'pointer',
          }}
        >
          Accept all
        </button>
      </div>
    </div>
  );
};

export default CookieBanner;
